import { useState } from "react";
import { RefreshCw, X, Pencil } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { userReportsApi } from "../../api";
import { useAsync } from "../../hooks";
import PinModal from "./PinModal";
import type { UserReport } from "../../types/reports";

type Row = Record<string, unknown>;

function fmt(val: unknown): string {
  if (val == null) return "—";
  if (typeof val === "number") {
    return Math.abs(val) >= 1_000_000
      ? (val / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + "M"
      : Math.abs(val) >= 1_000
      ? val.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : val.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(val);
}

function NumberTile({ rows, yCol }: { rows: Row[]; yCol?: string }) {
  const col = yCol || (rows[0] ? Object.keys(rows[0]).find((k) => typeof rows[0][k] === "number") || Object.keys(rows[0])[0] : null);
  const val = col ? rows[0]?.[col] : undefined;
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-4">
      <div className="text-4xl font-bold text-[#1D345F]">{fmt(val)}</div>
      {col && <div className="mt-1 text-xs text-gray-400">{col}</div>}
    </div>
  );
}

function TableTile({ rows }: { rows: Row[] }) {
  if (!rows.length) return <p className="text-xs text-gray-400 p-3">No results.</p>;
  const cols = Object.keys(rows[0]);
  return (
    <div className="flex-1 overflow-auto">
      <table className="min-w-full text-xs">
        <thead className="sticky top-0 bg-gray-50">
          <tr>
            {cols.map((c) => (
              <th key={c} className="whitespace-nowrap px-3 py-1.5 text-left font-semibold text-gray-600">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 20).map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {cols.map((c) => (
                <td key={c} className="whitespace-nowrap px-3 py-1 text-gray-700">{fmt(row[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 20 && (
        <p className="px-3 py-1 text-[10px] text-gray-400">Showing 20 of {rows.length} rows</p>
      )}
    </div>
  );
}

function BarTile({ rows, xCol, yCol }: { rows: Row[]; xCol: string; yCol: string }) {
  const data = rows.slice(0, 20).map((r) => ({ name: String(r[xCol] ?? ""), value: r[yCol] ?? 0 }));
  return (
    <div className="flex-1 min-h-0">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 32 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
          <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={fmt} />
          <Tooltip formatter={(v) => fmt(v)} />
          <Bar dataKey="value" name={yCol} fill="#1D345F" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LineTile({ rows, xCol, yCol }: { rows: Row[]; xCol: string; yCol: string }) {
  const data = rows.slice(0, 50).map((r) => ({ name: String(r[xCol] ?? ""), value: r[yCol] ?? 0 }));
  return (
    <div className="flex-1 min-h-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 32 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={Math.floor(data.length / 8)} />
          <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={fmt} />
          <Tooltip formatter={(v) => fmt(v)} />
          <Line type="monotone" dataKey="value" name={yCol} stroke="#1D345F" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface DashboardTileProps {
  report: UserReport;
  refreshKey?: number;
  onUnpinned: () => void;
}

export default function DashboardTile({ report, refreshKey = 0, onUnpinned }: DashboardTileProps) {
  const config = report.dashboard_config ?? {};
  const chartType = config.chart_type ?? "table";
  const xCol = config.x_col ?? "";
  const yCol = config.y_col ?? "";

  const [showEdit, setShowEdit] = useState(false);

  const { data: rows, loading, error, reload } = useAsync<Row[]>(
    () => userReportsApi.preview(report.id).then((res) => res.data?.rows ?? []),
    [report.id, refreshKey],
    { errorMessage: "Failed to load data" },
  );

  async function unpin() {
    await userReportsApi.pin(report.id, { pinned: false });
    onUnpinned();
  }

  return (
    <>
      <div className="flex flex-col h-full rounded-xl border border-gray-200 bg-white">
        {/* Header — also the drag handle for react-grid-layout */}
        <div className="drag-handle select-none cursor-grab active:cursor-grabbing flex shrink-0 items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
          <span className="truncate text-xs font-semibold text-gray-700">{report.name}</span>
          <div className="flex shrink-0 items-center gap-0.5">
            <button onClick={reload} title="Refresh" className="rounded p-1 text-gray-400 hover:text-gray-600">
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
            </button>
            <button onClick={() => setShowEdit(true)} title="Edit tile" className="rounded p-1 text-gray-400 hover:text-blue-600">
              <Pencil size={11} />
            </button>
            <button onClick={unpin} title="Remove from dashboard" className="rounded p-1 text-gray-400 hover:text-red-500">
              <X size={11} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden rounded-b-xl">
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <RefreshCw size={16} className="animate-spin text-gray-300" />
          </div>
        ) : error ? (
          <div className="flex flex-1 items-center justify-center px-4">
            <p className="text-center text-xs text-red-500">{error}</p>
          </div>
        ) : !rows?.length ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-xs text-gray-400">No data returned.</p>
          </div>
        ) : (
          <>
            {chartType === "number" && <NumberTile rows={rows} yCol={yCol} />}
            {chartType === "bar"    && <BarTile rows={rows} xCol={xCol} yCol={yCol} />}
            {chartType === "line"   && <LineTile rows={rows} xCol={xCol} yCol={yCol} />}
            {chartType === "table"  && <TableTile rows={rows} />}
          </>
        )}
        </div>
      </div>

      {showEdit && (
        <PinModal
          report={report}
          onClose={() => setShowEdit(false)}
          onSuccess={() => { setShowEdit(false); onUnpinned(); /* parent reloads */ }}
        />
      )}
    </>
  );
}
